import React, { useState, useRef, useEffect } from 'react';
import './BulkImport.css';

const STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error',
  SKIPPED: 'skipped',
};

const STATUS_ICON = {
  pending: '⏳',
  processing: '⚙️',
  success: '✅',
  error: '❌',
  skipped: '⏭️',
};

const POLL_INTERVAL_MS = 5000;   // check DB every 5s
const IMPORT_TIMEOUT_MS = 180000; // give up after 3 minutes

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export default function BulkImport() {
  const [inputText, setInputText]     = useState('');
  const [queue, setQueue]             = useState([]);
  const [running, setRunning]         = useState(false);
  const [done, setDone]               = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const cancelRef = useRef(false);
  const logEndRef = useRef(null);

  useEffect(() => {
    if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [queue]);

  // ── Parse textarea into import items ──────────────────────────────────────
  // Supports three formats (can be mixed freely):
  //   1. Plain URL                    → url_only
  //   2. URL + non-URL lines below    → url_with_text  (Facebook/YouTube + recipe text)
  //   3. Non-URL text block           → raw_text       (pasted recipe with no URL)
  const parseItems = (text) => {
    const lines = text.split('\n').map(l => l.replace(/^\[recipe\]\s*/i, '').trim());
    const items = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      if (!line) { i++; continue; }

      const isUrl = /^https?:\/\//i.test(line);

      if (isUrl) {
        const textLines = [];
        let j = i + 1;
        while (j < lines.length && lines[j] && !/^https?:\/\//i.test(lines[j])) {
          textLines.push(lines[j]);
          j++;
        }
        if (textLines.length > 0) {
          items.push({ url: line, pastedText: textLines.join('\n'), mode: 'url_with_text', display: line });
        } else {
          items.push({ url: line, pastedText: '', mode: 'url_only', display: line });
        }
        i = j;
      } else {
        const textLines = [line];
        let j = i + 1;
        while (j < lines.length && lines[j] && !/^https?:\/\//i.test(lines[j])) {
          textLines.push(lines[j]);
          j++;
        }
        items.push({ url: null, pastedText: textLines.join('\n'), mode: 'raw_text', display: textLines[0] });
        i = j;
      }
    }
    return items;
  };

  // ── Start button ──────────────────────────────────────────────────────────
  const handleStart = () => {
    const items = parseItems(inputText);
    if (items.length === 0) { alert('Please enter at least one recipe URL or text.'); return; }
    if (items.length > 25 && !window.confirm(`You entered ${items.length} recipes (~2 min each). Continue?`)) return;

    cancelRef.current = false;
    const queueItems = items.map((item, idx) => ({
      id: idx, ...item, status: STATUS.PENDING, message: '', recipeUrl: null,
    }));
    setQueue(queueItems);
    setRunning(true);
    setDone(false);
    setCurrentIndex(0);
    runSequential(queueItems);
  };

  const handleCancel = () => { cancelRef.current = true; };

  const handleReset = () => {
    setQueue([]); setInputText(''); setRunning(false); setDone(false); setCurrentIndex(0);
  };

  // ── Core: process one item at a time ─────────────────────────────────────
  const runSequential = async (items) => {
    const list = [...items];

    for (let i = 0; i < list.length; i++) {
      if (cancelRef.current) {
        for (let j = i; j < list.length; j++) {
          list[j] = { ...list[j], status: STATUS.SKIPPED, message: 'Cancelled' };
        }
        setQueue([...list]);
        break;
      }

      // Mark as processing
      list[i] = { ...list[i], status: STATUS.PROCESSING, message: 'Sending to n8n...' };
      setQueue([...list]);
      setCurrentIndex(i);

      try {
        const result = await importOne(list[i]);
        list[i] = {
          ...list[i],
          status: result.skipped ? STATUS.SKIPPED : STATUS.SUCCESS,
          message: result.message,
          recipeUrl: result.recipeUrl || null,
        };
      } catch (err) {
        list[i] = { ...list[i], status: STATUS.ERROR, message: err.message || 'Import failed' };
        if (err.message === 'Cancelled') {
          for (let j = i + 1; j < list.length; j++) {
            list[j] = { ...list[j], status: STATUS.SKIPPED, message: 'Cancelled' };
          }
          setQueue([...list]);
          break;
        }
      }

      setQueue([...list]);
    }

    setRunning(false);
    setDone(true);
  };

  // ── Trigger a single import then poll until it appears in the DB ──────────
  const importOne = async (item) => {
    const token = localStorage.getItem('rs_token');

    let message;
    if (item.mode === 'url_with_text') message = '[recipe] ' + item.url + '\n' + item.pastedText;
    else if (item.mode === 'raw_text')  message = '[recipe] ' + item.pastedText;
    else                                message = '[recipe] ' + item.url;

    // 1. Trigger the import (send one URL to n8n)
    const triggerRes = await fetch('/api/recipes/bulk-import', {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ urls: [item.url || item.pastedText], messages: [message] }),
    });

    const triggerData = await triggerRes.json();
    if (!triggerData.success) throw new Error(triggerData.error || 'Failed to queue');

    const triggered = triggerData.triggered?.[0];
    if (!triggered || triggered.triggered === false) throw new Error('n8n did not accept the request');

    // 2. For URL-based imports, poll until the recipe appears in the DB
    if (item.url) {
      const deadline = Date.now() + IMPORT_TIMEOUT_MS;

      while (Date.now() < deadline) {
        if (cancelRef.current) throw new Error('Cancelled');
        await sleep(POLL_INTERVAL_MS);

        const checkRes = await fetch(
          '/api/recipes/check-url?url=' + encodeURIComponent(item.url),
          { headers: { 'Authorization': 'Bearer ' + token } }
        );

        if (checkRes.ok) {
          const checkData = await checkRes.json();
          if (checkData.found) {
            return { message: '✓ Imported successfully', recipeUrl: checkData.recipeUrl };
          }
        }
      }

      // Timed out — recipe is probably still processing (video/Facebook can take longer)
      return { message: 'Queued — check Telegram for status', recipeUrl: null };
    }

    // 3. raw_text mode has no URL to poll against — trust the queue
    return { message: 'Queued! Watch Telegram for confirmation', recipeUrl: null };
  };

  // ── Derived counts ────────────────────────────────────────────────────────
  const parsedItems = parseItems(inputText);
  const urlCount    = parsedItems.filter(i => i.mode === 'url_only').length;
  const mixedCount  = parsedItems.filter(i => i.mode === 'url_with_text').length;
  const textCount   = parsedItems.filter(i => i.mode === 'raw_text').length;

  const counts = {
    total:   queue.length,
    success: queue.filter(i => i.status === STATUS.SUCCESS).length,
    error:   queue.filter(i => i.status === STATUS.ERROR).length,
    skipped: queue.filter(i => i.status === STATUS.SKIPPED).length,
  };
  const processed = counts.success + counts.error + counts.skipped;
  const progress  = counts.total > 0 ? Math.round((processed / counts.total) * 100) : 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="bulk-import">
      <div className="container">

        <div className="bulk-import__header">
          <h1>📋 Bulk Recipe Import</h1>
          <p>
            Paste up to 25 recipes below. Recipes are processed <strong>one at a time</strong> —
            each fully completes before the next starts. Allow ~2 minutes per recipe.
            You'll get a Telegram message when each one finishes.
          </p>
          <div className="bulk-import__formats">
            <div className="bulk-import__format">
              <strong>🔗 URL only</strong>
              <code>https://www.budgetbytes.com/gumbo/</code>
            </div>
            <div className="bulk-import__format">
              <strong>🎥 Video URL + recipe text</strong>
              <code>https://facebook.com/reel/123</code>
              <code>Chicken Tacos</code>
              <code>2 cups chicken, 1 tsp cumin...</code>
            </div>
            <div className="bulk-import__format">
              <strong>📝 Pasted text only</strong>
              <code>Crockpot Chicken Spaghetti</code>
              <code>3 chicken breasts, 20oz cream of chicken...</code>
            </div>
          </div>
        </div>

        {!running && !done && (
          <div className="bulk-import__input-section">
            <label className="bulk-import__label">
              Recipes <span className="bulk-import__hint">(mix URLs and pasted text freely)</span>
            </label>
            <textarea
              className="bulk-import__textarea"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              placeholder={
                'https://www.allrecipes.com/recipe/123/\n' +
                'https://facebook.com/reel/456\n' +
                'Chicken Tacos\n2 lbs chicken\n1 tsp cumin...\n' +
                'https://www.budgetbytes.com/gumbo/'
              }
              rows={12}
            />
            <div className="bulk-import__input-footer">
              <span className="bulk-import__count">
                {parsedItems.length > 0 ? (
                  <>
                    {parsedItems.length} recipe{parsedItems.length !== 1 ? 's' : ''} detected
                    {urlCount   > 0 && <span> · {urlCount} URL{urlCount !== 1 ? 's' : ''}</span>}
                    {mixedCount > 0 && <span> · {mixedCount} video+text</span>}
                    {textCount  > 0 && <span> · {textCount} text-only</span>}
                  </>
                ) : 'No recipes detected yet'}
              </span>
              <button
                className="btn btn-primary bulk-import__start-btn"
                onClick={handleStart}
                disabled={parsedItems.length === 0}
              >
                🚀 Start Import
              </button>
            </div>
          </div>
        )}

        {(running || done) && (
          <div className="bulk-import__progress-section">

            <div className="bulk-import__summary">
              <div className="bulk-import__summary-stat">
                <span className="bulk-import__summary-num">{counts.total}</span>
                <span className="bulk-import__summary-label">Total</span>
              </div>
              <div className="bulk-import__summary-stat bulk-import__summary-stat--success">
                <span className="bulk-import__summary-num">{counts.success}</span>
                <span className="bulk-import__summary-label">Done</span>
              </div>
              <div className="bulk-import__summary-stat bulk-import__summary-stat--error">
                <span className="bulk-import__summary-num">{counts.error}</span>
                <span className="bulk-import__summary-label">Failed</span>
              </div>
              {counts.skipped > 0 && (
                <div className="bulk-import__summary-stat">
                  <span className="bulk-import__summary-num">{counts.skipped}</span>
                  <span className="bulk-import__summary-label">Skipped</span>
                </div>
              )}
            </div>

            <div className="bulk-import__progress-bar">
              <div className="bulk-import__progress-fill" style={{ width: `${progress}%` }} />
            </div>

            <div className="bulk-import__progress-label">
              {done
                ? `Done! ${counts.success} recipe${counts.success !== 1 ? 's' : ''} imported.`
                : `Processing ${currentIndex + 1} of ${counts.total}...`}
            </div>

            <div className="bulk-import__controls">
              {running && (
                <button className="btn btn-outline" onClick={handleCancel}>
                  ✋ Cancel Remaining
                </button>
              )}
              {done && (
                <button className="btn btn-outline" onClick={handleReset}>
                  ↩️ Import More
                </button>
              )}
            </div>

            <div className="bulk-import__log">
              {queue.map((item) => (
                <div
                  key={item.id}
                  className={`bulk-import__log-item bulk-import__log-item--${item.status}`}
                >
                  <span className="bulk-import__log-icon">{STATUS_ICON[item.status]}</span>
                  <div className="bulk-import__log-content">
                    <span className="bulk-import__log-url">
                      {item.display && item.display.length > 65
                        ? item.display.substring(0, 65) + '...'
                        : item.display}
                    </span>
                    {item.mode !== 'url_only' && (
                      <span className="bulk-import__log-mode">
                        {item.mode === 'url_with_text' ? '🎥 Video + text' : '📝 Text only'}
                      </span>
                    )}
                    {item.message && (
                      <span className="bulk-import__log-msg">{item.message}</span>
                    )}
                    {item.recipeUrl && (
                      <a
                        className="bulk-import__log-link"
                        href={item.recipeUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Recipe →
                      </a>
                    )}
                  </div>
                  {item.status === STATUS.PROCESSING && (
                    <span className="bulk-import__log-spinner" />
                  )}
                </div>
              ))}
              <div ref={logEndRef} />
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
